import Matter from "matter-js";
import type { BenchScene } from "./scenes";
import type { EngineRunner } from "./runner";

// Matter.js works in pixel space; Fast2D works in meters. We build Matter at
// `SCALE` px per meter and convert back to meters when rendering, so a single
// world-space camera draws both engines identically.
const SCALE = 30;

// Matter integrates gravity as a position delta of
// `gravity.y * gravity.scale * deltaMs²` per step. To make a body fall at the
// real `gravity.y` m/s² at `SCALE` px/m, set `gravity.y` in m/s² and pick a
// scale of `SCALE / 1000²` (the 1000² converts the ms-based delta to seconds).
// This is timestep-independent and keeps Matter falling in lock-step with the
// other engines instead of ~3× too fast (the old hand-tuned 0.0009).
const GRAVITY_SCALE = SCALE / 1_000_000;

/** Build a Matter.js runner for a benchmark scene (sleeping off for fair throughput). */
export function createMatterRunner(scene: BenchScene): EngineRunner {
	const engine = Matter.Engine.create();
	engine.enableSleeping = false;
	engine.gravity.x = 0;
	engine.gravity.y = scene.gravityY;
	engine.gravity.scale = GRAVITY_SCALE;

	const bodies: Matter.Body[] = [];
	let count = 0;

	scene.build((s) => {
		const opts: Matter.IBodyDefinition = {
			isStatic: !!s.isStatic,
			friction: s.friction ?? 0.4,
			restitution: s.restitution ?? 0,
		};
		const body =
			s.kind === "circle"
				? Matter.Bodies.circle(s.x * SCALE, s.y * SCALE, s.r! * SCALE, opts)
				: s.kind === "triangle"
					? Matter.Bodies.fromVertices(
							s.x * SCALE,
							s.y * SCALE,
							[s.verts!.map((v) => ({ x: v[0] * SCALE, y: v[1] * SCALE }))],
							opts,
						)
					: Matter.Bodies.rectangle(
							s.x * SCALE,
							s.y * SCALE,
							s.w! * SCALE,
							s.h! * SCALE,
							opts,
						);
		bodies.push(body);
		count++;
	});
	Matter.Composite.add(engine.world, bodies);

	return {
		name: "Matter.js",
		get bodyCount() {
			return count;
		},
		step(dt: number): void {
			// Matter expects the delta in milliseconds.
			Matter.Engine.update(engine, dt * 1000);
		},
		forEachBody(cb): void {
			const all = Matter.Composite.allBodies(engine.world);
			for (let i = 0; i < all.length; i++) {
				const b = all[i]!;
				// Matter sets `circleRadius` to 0 on non-circle bodies (not null),
				// so only treat it as a circle when the radius is positive —
				// otherwise boxes render as invisible zero-radius dots.
				if (b.circleRadius && b.circleRadius > 0) {
					cb({
						kind: "circle",
						x: b.position.x / SCALE,
						y: b.position.y / SCALE,
						r: b.circleRadius / SCALE,
						angle: b.angle,
						isStatic: b.isStatic,
					});
				} else {
					const v = b.vertices;
					const verts: number[] = new Array(v.length * 2);
					for (let j = 0; j < v.length; j++) {
						verts[j * 2] = v[j]!.x / SCALE;
						verts[j * 2 + 1] = v[j]!.y / SCALE;
					}
					cb({ kind: "poly", verts, isStatic: b.isStatic });
				}
			}
		},
		dispose(): void {
			Matter.Composite.clear(engine.world, false, true);
			Matter.Engine.clear(engine);
			bodies.length = 0;
		},
	};
}
